import type {
  AudioAssetItem,
  AudioPresentationType,
  AudioAssetSummary,
  CanvasVoiceTemplateListItemDto,
  VoiceTemplateGenerationPreference,
} from '../../api'

/** Which domain this picker shell is selecting for — shared UI, separate datasources. */
export type AudioAssetPickerDomain = 'audio_asset' | 'voice_template'

/** Canonical selection contract for Canvas Audio Asset Picker bindings. */
export interface AudioAssetPickerSelection {
  assetId: string
  presentationType: AudioPresentationType
  name: string
  voiceTemplateId: string | null
  playbackUrl: string | null
  usable: boolean
  // VistoeraTASK-00217 — the newly-selected Voice Template's OWN
  // remembered Pitch/Speed, carried straight through from the list row
  // the user just clicked so applying "Voice Template Parameter Memory"
  // on bind never needs an extra network round trip.
  generationPreference?: VoiceTemplateGenerationPreference | null
}

export const AUDIO_PICKER_DISPLAY_MODE = 'LIST_ONLY' as const

export type AudioPickerCategoryKey =
  | 'all'
  | 'voice_template'
  | 'speech'
  | 'music'
  | 'sfx'
  | 'ambience'
  | 'recording'
  | 'reference_audio'
  | 'mixdown'

export const AUDIO_PICKER_CATEGORY_ORDER: readonly AudioPickerCategoryKey[] = [
  'all',
  'voice_template',
  'speech',
  'music',
  'sfx',
  'ambience',
  'recording',
  'reference_audio',
  'mixdown',
]

const CATEGORY_TO_PRESENTATION: Record<Exclude<AudioPickerCategoryKey, 'all'>, AudioPresentationType> = {
  voice_template: 'VOICE_TEMPLATE',
  speech: 'SPEECH',
  music: 'MUSIC',
  sfx: 'SFX',
  ambience: 'AMBIENCE',
  recording: 'RECORDING',
  reference_audio: 'REFERENCE_AUDIO',
  mixdown: 'MIXDOWN',
}

const PRESENTATION_TO_CATEGORY: Record<AudioPresentationType, Exclude<AudioPickerCategoryKey, 'all'>> = {
  VOICE_TEMPLATE: 'voice_template',
  SPEECH: 'speech',
  MUSIC: 'music',
  SFX: 'sfx',
  AMBIENCE: 'ambience',
  RECORDING: 'recording',
  REFERENCE_AUDIO: 'reference_audio',
  MIXDOWN: 'mixdown',
}

export const AUDIO_PICKER_DEFAULT_PAGE_SIZE = 20

const VOICE_TEMPLATE_PREFIX = 'voice_template:'

export function parseVoiceTemplateIdFromAudioAssetId(assetId: string): string | null {
  if (!assetId.startsWith(VOICE_TEMPLATE_PREFIX)) return null
  const id = assetId.slice(VOICE_TEMPLATE_PREFIX.length).trim()
  return id || null
}

export function toAudioAssetPickerSelection(item: AudioAssetItem): AudioAssetPickerSelection {
  return {
    assetId: item.id,
    presentationType: item.presentation_type,
    name: item.name,
    voiceTemplateId: parseVoiceTemplateIdFromAudioAssetId(item.id),
    playbackUrl: item.playback_url,
    usable: item.usable,
    generationPreference: item.generation_preference ?? null,
  }
}

/** Map canonical Voice Template list API rows into the shared picker row shape. */
export function voiceTemplateListItemToAudioAssetItem(dto: CanvasVoiceTemplateListItemDto): AudioAssetItem {
  return {
    id: `${VOICE_TEMPLATE_PREFIX}${dto.template_id}`,
    presentation_type: 'VOICE_TEMPLATE',
    name: dto.name || '未命名声音模板',
    description: dto.voice_brief,
    source: 'CANVAS',
    ownership: 'WORKSPACE_OWNED',
    status: dto.status ?? dto.creation_status ?? 'SUCCEEDED',
    usable: Boolean(dto.usable ?? dto.selectable),
    duration_ms: null,
    has_preview_audio: Boolean(dto.preview_audio?.playback_url),
    playback_url: dto.preview_audio?.playback_url ?? null,
    provider: dto.provider,
    created_at: dto.created_at ?? dto.updated_at,
    updated_at: dto.updated_at,
    size_bytes: dto.preview_audio?.size_bytes ?? null,
    filename: dto.preview_audio?.filename ?? null,
    can_delete: false,
    usage_count: null,
    tags: [],
    project_id: dto.project_id,
    canvas_node_id: dto.canvas_node_id,
    voice_brief: dto.voice_brief,
    preview_duration_ms: dto.preview_audio?.duration_ms ?? null,
    execution_availability: dto.execution_availability,
    execution_availability_reason: dto.execution_availability_reason ?? null,
    generation_preference: dto.generation_preference ?? null,
  }
}

export function voiceTemplatePickerSummaryFromTotal(total: number): AudioAssetSummary {
  return {
    all: total,
    voice_template: total,
    speech: 0,
    music: 0,
    sfx: 0,
    ambience: 0,
    recording: 0,
    reference_audio: 0,
    mixdown: 0,
  }
}

export function audioAssetPickerInitialSelectionFromVoiceTemplateId(
  voiceTemplateId: string | null | undefined,
): string | null {
  if (!voiceTemplateId) return null
  return `${VOICE_TEMPLATE_PREFIX}${voiceTemplateId}`
}

export function audioAssetPickerInitialSelectionFromReferenceAssetId(
  assetId: string | null | undefined,
): string | null {
  const id = (assetId || '').trim()
  return id || null
}

export function presentationTypeFromCategoryKey(
  key: AudioPickerCategoryKey,
): AudioPresentationType | undefined {
  if (key === 'all') return undefined
  return CATEGORY_TO_PRESENTATION[key]
}

export function categoryKeyFromPresentationType(type: AudioPresentationType): Exclude<AudioPickerCategoryKey, 'all'> {
  return PRESENTATION_TO_CATEGORY[type]
}

/** Resolve sidebar categories — when locked, only show allowed types (+ all if multiple). */
export function resolvePickerCategories(
  allowedCategories?: AudioPresentationType[] | null,
  lockPresentationType?: boolean,
  legacyPresentationType?: AudioPresentationType,
): AudioPickerCategoryKey[] {
  const locked =
    allowedCategories && allowedCategories.length > 0
      ? allowedCategories
      : lockPresentationType && legacyPresentationType
        ? [legacyPresentationType]
        : null

  if (!locked) return [...AUDIO_PICKER_CATEGORY_ORDER]
  if (locked.length === 1) return [categoryKeyFromPresentationType(locked[0])]
  const keys = locked.map((t) => categoryKeyFromPresentationType(t))
  return ['all', ...keys]
}

export function resolveInitialCategory(
  initialCategory?: AudioPresentationType | 'all' | null,
  legacyPresentationType?: AudioPresentationType,
  allowedCategories?: AudioPresentationType[] | null,
  lockPresentationType?: boolean,
): AudioPickerCategoryKey {
  if (initialCategory === 'all') return 'all'
  if (initialCategory) return categoryKeyFromPresentationType(initialCategory)

  const locked =
    allowedCategories && allowedCategories.length > 0
      ? allowedCategories
      : lockPresentationType && legacyPresentationType
        ? [legacyPresentationType]
        : null

  if (locked?.length === 1) return categoryKeyFromPresentationType(locked[0])
  if (legacyPresentationType) return categoryKeyFromPresentationType(legacyPresentationType)
  return 'all'
}

export function summaryCountForCategory(summary: AudioAssetSummary | null, key: AudioPickerCategoryKey): number | null {
  if (!summary) return null
  if (key === 'all') return summary.all
  return summary[key] ?? null
}

/** Reuse backend canonical `usable` flag (creation lifecycle + execution
 * availability). Accepts `null`/`undefined` defensively — VistoeraTASK-00113
 * root cause: `AudioAssetPicker`'s `confirmLabel` called this with a
 * non-null-asserted `selected` that legitimately IS `null` right after the
 * picker opens with a non-null `initialSelection` (an already-selected
 * voice template on the node) while `items` is still `[]`/hasn't loaded the
 * referenced asset yet — an uncaught `TypeError: Cannot read properties of
 * null (reading 'usable')` that bubbled to the Canvas root
 * `CanvasErrorBoundary` and blanked the ENTIRE Canvas ("画布加载失败").
 * A missing/not-yet-loaded/deleted asset is, by definition, not
 * selectable — never throw, just say so. */
export function isAudioAssetSelectable(item: AudioAssetItem | null | undefined): boolean {
  return Boolean(item?.usable)
}

export function failedVoiceTemplateTooltipKey(item: AudioAssetItem): string | null {
  if (item.presentation_type !== 'VOICE_TEMPLATE') return null
  if (item.usable) return null
  if (item.status === 'FAILED') return 'canvas:audioAssetPicker.failedVoiceTemplateTooltip'
  return 'canvas:audioAssetPicker.unavailableVoiceTemplateTooltip'
}
